// =============================================================================
// VITE ENTRY POINT - Main bootstrapping file for Indexed.IO
// =============================================================================
// This file replaces Grunt's script concatenation and manual HTML <script> tags

// ===== EXTERNAL DEPENDENCIES (in dependency order) =====

// MUST be the first import: sets window.jQuery before Angular evaluates.
// Angular's bindJQuery() fires at module-evaluation time (not at bootstrap
// time), so window.jQuery must be set as a side-effect of a module that is
// imported before the angular import chain. Moving this to main.js body
// would be too late — the body only runs after all imports have evaluated.
import './scripts/setup-globals';

// Core dependencies
import 'uuidjs';
import 'jquery';
import 'jquery-ui/dist/jquery-ui';
import './vendor/intlTelInput-jquery.js';
import moment from 'moment';
import 'moment-timezone';
// Expose moment as a browser global so UMD libraries (e.g. datetimepicker) can find it
window.moment = moment;

// Angular Core & Official Modules
import 'angular';
import 'angular-route';
import 'angular-resource';
import 'angular-cookies';
import 'angular-animate';

// Angular Third-party Modules
import 'angular-ui-bootstrap/dist/ui-bootstrap-tpls';
import 'angular-ui-sortable';
import 'angular-ui-utils/modules/mask/mask.js';
import 'angular-hotkeys';
import 'underscore';
import he from 'he';
// Expose he as a browser global so grid renderers can find it
window.he = he;
import 'angular-bootstrap-colorpicker';
import 'bootstrap-slider';
import './vendor/bootstrap-datetimepicker.js';

// Charting & Data Libraries
import * as d3 from 'd3';
// Expose d3 as browser global so chart directives (timeline.ts, chord.ts) can find it
window.d3 = d3;
import c3 from 'c3';
import 'c3/c3.css';
// Expose c3 as browser global so UMD-dependent directives (e.g. pie.ts, gauge.ts) can find it
window.c3 = c3;
import 'jsonformatter';

// Rich Text Editor
import './scripts/vendor/angular-compat-shim.js'; // restore angular.lowercase/uppercase removed in Angular 1.7
import 'textangular/dist/textAngular-sanitize.min.js'; // textAngular's own $sanitize (sets version='taSanitize')
import 'textangular';

// Video Player
import 'videogular';
import 'videogular-controls';
import 'videogular-overlay-play';
import 'videogular-poster';
import 'videogular-buffering';

// Data Grids & Tables
import './scripts/ag-grid-init.ts';
import 'angular-smart-table';
import 'angular-filter';
import 'checklist-model';
import 'clipboard';
import 'ngclipboard';

// Magicsuggest autocomplete
import 'magicsuggest';

// Additional Angular modules
import '@stomp/stompjs';
import 'ng-intl-tel-input/ng-intl-tel-input.module.js';
import 'ng-intl-tel-input/ng-intl-tel-input.provider.js';
import 'ng-intl-tel-input/ng-intl-tel-input.directive.js';
import '@stripe/stripe-js';

// Custom vendor scripts (must come after external libs they depend on)
import './scripts/vendor/pusher.color.js';
import './scripts/vendor/controls/angular-toggle-switch.min.js';
import './scripts/vendor/l10n.js';
import './scripts/vendor/pdfviewer/custom/pdf.js';
import './scripts/vendor/pdfviewer/compatibility.js';
import './scripts/vendor/pdfviewer/custom/viewer.js';
import './scripts/vendor/pdfviewer/custom/redact.js';
import './scripts/plugins/textAngularPlugin.js';

// ===== APPLICATION CODE (TypeScript compiled output) =====

// Core utilities & models (kept: window.Utilities side-effect)
import './scripts/Utilities.ts';

// Angular app module (declares frontendApp)
import './scripts/app-module.ts';

// All services (coordinated imports with proper dependency order)
import './scripts/services/index.ts';

// App configuration and routes
import './scripts/frontend.ts';

// HTTP Interceptors
import './scripts/providers/httpinterceptor.js';
import './scripts/providers/auth-interceptor.ts';
import './scripts/providers/ajax-dedup.ts';

// Model files with window.app.* side-effects (must stay — plain-JS files read these at runtime)
import './scripts/model/project/Status.ts';
import './scripts/model/search/ISearch.ts';

// All other model files are pure ESM — imported transitively by their consumers (services/controllers/directives)
// and do not need to be explicitly imported here.

// Services are now imported via services/index.ts for proper dependency coordination

// Directives - all TypeScript directives imported via barrel export
import './scripts/directives/index.ts';

// React directive registrations (react2angular wrappers)
import './scripts/directives/react/index.ts';

// Angular → React bridge: exposes Angular injector to React code
import { setAngularInjector } from './src/services/angular-bridge';
import { loadSystemMessages, isMaintenanceActive, isMaintenanceBypassHost } from './src/services/systemMessages';
import { mountMaintenancePage } from './src/pages/MaintenancePage';

// Vendor & Legacy JavaScript Directives (not yet converted to TypeScript)
import './scripts/vendor/charting/custom/liquidFillGauge.js';
import './scripts/directives/utilities/equal.js';
import './scripts/directives/utilities/bindHtml.js';
import './scripts/directives/controls/star.js';
import './scripts/directives/authentication/role.js';
import './scripts/directives/controls/toggle.js';
import './scripts/directives/notification/notifications.js';
import './scripts/directives/notification/notification.js';
import './scripts/directives/charting/area.js';
import './scripts/directives/charting/column.js';
import './scripts/directives/controls/droptarget.js';
import './scripts/directives/notification/inputNotice.js';
import './scripts/directives/utilities/filter.js';
import './scripts/legacy-js-globals.ts';
import './scripts/directives/controls/navigation.js';
import './scripts/directives/controls/href.js';
import './scripts/directives/tagging/tag.js';
import './scripts/directives/tagging/tagPopoverPopup.js';
import './scripts/directives/tagging/tagPopover.js';
import './scripts/directives/tagging/tagBoolean.js';
import './scripts/directives/tagging/tagBooleanItem.js';
import './scripts/directives/theming/theme.js';
import './scripts/directives/theming/theme_image.js';
import './scripts/directives/theming/theme_powered_by.js';
import './scripts/directives/controls/popoverPopup.js';
import './scripts/directives/controls/popover.js';
import './scripts/directives/utilities/charCode.js';

// Filters
import './scripts/filters/bytes.ts';
import './scripts/filters/titlecase.ts';
import './scripts/filters/chatparticipants.ts';
import './scripts/filters/truncate.js';
import './scripts/filters/pad.js';
import './scripts/filters/ucwords.js';
import './scripts/filters/newlines.js';

// Controllers (must come after services and directives)
// TypeScript controllers — all have frontendApp.controller(...) side-effects; consolidated via barrel
import './scripts/controllers/index.ts';
// Legacy JS controllers — not yet converted to TypeScript, must remain here
// settings.js deleted — page migrated to React (SettingsPage.tsx)
// organization.js deleted — page migrated to React (OrganizationPage.tsx)
import './scripts/controllers/upload.js';
import './scripts/controllers/import.js';
import './scripts/controllers/load_file.js';
import './scripts/controllers/notification.js';
import './scripts/controllers/trial_warning.js';
import './scripts/controllers/reset_password_button.js';
import './scripts/controllers/add_account_modal.js';
import './scripts/controllers/reset_password_modal.js';
import './scripts/controllers/add_custodian_modal.js';
import './scripts/controllers/add_tag_modal.js';
import './scripts/controllers/reporting/add_report_modal.js';
import './scripts/controllers/reporting/add_tag_report_modal.js';
import './scripts/controllers/add_user_modal.js';
import './scripts/controllers/request_reviewer_modal.js';
import './scripts/controllers/confirm_modal.js';
import './scripts/controllers/browser_modal.js';
import './scripts/controllers/layer.js';
// 404.js deleted — page migrated to React (NotFoundPage.tsx)

// Chat widget show/hide helpers (Teams widget injection, gated by subdomain)
import './scripts/chat-widget.js';

// ===== STYLES =====
// Third-party CSS (resolved by Vite from node_modules)
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/css/bootstrap-theme.css';
import '@fortawesome/fontawesome-free/css/all.css';
import 'angular-ui-bootstrap/dist/ui-bootstrap-csp.css';
import 'angular-hotkeys/build/hotkeys.css';
import './vendor/intlTelInput.css';
import 'jquery-ui/themes/base/all.css';
import 'jquery-ui/themes/base/theme.css';
import './vendor/bootstrap-datetimepicker.css';
import 'bootstrap-slider/dist/css/bootstrap-slider.css';
import 'c3/c3.css';
import 'textangular/dist/textAngular.css';
import 'angular-bootstrap-colorpicker/css/colorpicker.css';
import 'jsonformatter/dist/json-formatter.css';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-bootstrap.css';
// App SCSS (variables, mixins, ag-grid, partials)
import './styles/main.scss';

// ===== ANGULAR BOOTSTRAP =====
// Manual bootstrap after all modules are registered — must be here at the
// bottom of main.js (not in index.html as ng-app) because type="module"
// scripts execute asynchronously. Angular's auto-bootstrap fires on
// DOMContentLoaded before ES modules have evaluated, so frontendApp would
// not yet be registered. By bootstrapping here we guarantee all imports
// above have completed before angular.bootstrap() is called.
document.addEventListener('DOMContentLoaded', async () => {
  const env = import.meta.env.VITE_ENVIRONMENT || 'prod';
  fetch('https://iio-assets.s3.amazonaws.com/features/features.json')
    .then(r => r.json())
    .then(data => {
      window.__features = data[env] || {};
    })
    .catch(() => {
      window.__features = {};
    })
    .finally(() => {
      window.dispatchEvent(new CustomEvent('featuresLoaded', { detail: window.__features }));
    });

  const messages = await loadSystemMessages();
  window.__systemMessages = messages;

  // The blue/green slot stays reachable while prod shows the outage page.
  let maintenanceActive = isMaintenanceActive(messages) && !isMaintenanceBypassHost();

  // Dev/QA override so maintenance mode can be tested without editing messages.json.
  // Hash-based routing means the query string must precede the hash: host/?maintenance=1#!/project.
  const NON_PROD_ENVS = ['localhost', 'dev', 'stg'];
  if (NON_PROD_ENVS.includes(env)) {
    const override = new URLSearchParams(window.location.search).get('maintenance');
    if (override === '1') maintenanceActive = true;
    else if (override === '0') maintenanceActive = false;
  }

  if (maintenanceActive) {
    // Skip Angular bootstrap entirely: the API is down during an outage, so
    // bootstrapping would fire account/token/theme requests that all fail
    // and flood the user with error toasts.
    mountMaintenancePage();
    return;
  }

  const appElement = document.getElementById('ng-app') || document.documentElement;
  const injector = angular.bootstrap(appElement, ['frontendApp']);
  setAngularInjector(injector);
});
